import { memo, useEffect } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { is3pProductKey, ProductKeys } from '../../../common/constants/products';
import { DEFAULT_BULK_CONNECT_BANNER_STATE } from '../../../common/utils/bulk-connect-banner';
import { useLocalStorage } from '../../store/external-storage';
import { isProductKeyWithTimestampMap, type ProductKeyWithTimestampMap } from '../index';

type UpdateBulkConnectLocalStorageProps = {
	accessibleProducts: ProductKeys[] | undefined;
	isConfigLoaded: boolean;
};

export const UpdateBulkConnectLocalStorage = memo(
	({ accessibleProducts, isConfigLoaded }: UpdateBulkConnectLocalStorageProps) => {
		const [localStorageState, setLocalStorageState] = useLocalStorage();

		useEffect(() => {
			if (!isConfigLoaded) {
				return;
			}

			if (fg('3p_bulk_connect')) {
				const thirdPartyProductKeys =
					accessibleProducts?.filter((product) => is3pProductKey(product)) || [];

				const currentTime = Date.now().toString();
				let hasNewProducts = false;

				const newState = {
					...localStorageState,
					previous3pProducts: localStorageState?.previous3pProducts ?? {},
				};
				if (!localStorageState || !localStorageState.previous3pProducts) {
					// Local storage does not store this information yet. We assume this is the first time a user is seeing these products, and store all products with current timestamp
					newState.previous3pProducts = thirdPartyProductKeys.reduce(
						(acc, product) => ({
							...acc,
							[product]: currentTime,
						}),
						{} as ProductKeyWithTimestampMap,
					);
					hasNewProducts = true;
				} else {
					// Compare with previous state and store newly visible products with current timestamp, while keeping the previous timestamps for existing products
					const previous3pProducts: ProductKeyWithTimestampMap = isProductKeyWithTimestampMap(
						localStorageState.previous3pProducts,
					)
						? localStorageState.previous3pProducts
						: {};

					thirdPartyProductKeys.forEach((product) => {
						if (!is3pProductKey(product)) {
							return;
						}
						if (!previous3pProducts[product]) {
							newState.previous3pProducts[product] = currentTime;
							hasNewProducts = true;
						} else {
							newState.previous3pProducts[product] = previous3pProducts[product];
						}
					});
				}
				if (hasNewProducts) {
					// if there are new products, we need to reset the secondary banner dismissal state
					newState.bulkConnectBanner = {
						...(localStorageState?.bulkConnectBanner || DEFAULT_BULK_CONNECT_BANNER_STATE),
						secondaryBannerDismissed: false,
					};
				}

				setLocalStorageState(newState);
			}
		}, [accessibleProducts, isConfigLoaded, localStorageState, setLocalStorageState]);

		return null;
	},
);
